import { inject, Injectable } from '@angular/core';

import { MsalService } from '@azure/msal-angular';
import { environment } from '../../../environments/environment';
import { ENDPOINT_CONSTANTS } from '../constants/endpoint.constants';
import { HttpCrudService } from './http-crud.service';
import { UserPermission } from '../constants/app.constants';
import { Observable, of, catchError, tap, map } from 'rxjs';

export type UserDetails = {
  name: string;
  email: string;
  permissions: string[];
};

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  loggedInUser: UserDetails = { name: '', email: '', permissions: [] };
  httpCrudService = inject(HttpCrudService);
  private permissionsLoaded = false;

  constructor(private readonly authService: MsalService) {}

  /**
   * Retrieves the name of the currently logged-in user.
   *
   * @returns {string} The name of the first account returned by the authentication service.
   */
  getLoggedInUserName(): string {
    return this.authService.instance?.getAllAccounts()[0]?.name ?? '';
  }

  getUserPermissions(): Observable<any> {
    const authInstance = this.authService.instance?.getAllAccounts()[0];
    this.loggedInUser.name = authInstance?.name || '';
    this.loggedInUser.email = authInstance?.localAccountId || '';
    const url = `${environment.CDM_SERVICE.BASE_URL}${ENDPOINT_CONSTANTS.CDM_PERMISSION_API(this.loggedInUser.email)}`;
    return this.httpCrudService.get<any>(url);
  }

  hasEditPermission(): boolean {
    return this.loggedInUser.permissions.some(
      (per) => per === UserPermission.EDIT_ALL,
    );
  }

  /**
   * Checks if the logged-in user has any of the provided permissions.
   *
   * @param permissions - Array of permission strings to check against
   * @returns {boolean} True if user has at least one of the provided permissions, false otherwise
   */
  hasAnyPermission(permissions: string[]): boolean {
    if (!permissions || permissions.length === 0) {
      return false;
    }
    return this.loggedInUser.permissions.some((userPermission) =>
      permissions.includes(userPermission),
    );
  }

  /**
   * Loads user permissions from the API and updates loggedInUser.
   *
   * @returns {Observable<void>} Observable that completes when permissions are loaded
   */
  loadAndSetPermissions(): Observable<void> {
    // If permissions already loaded, return immediately
    if (this.permissionsLoaded) {
      return of(void 0);
    }
    return this.getUserPermissions().pipe(
      tap((res: any) => {
        const permissionsArray =
          res?.users?.[0]?.applicationCode_PermissionCode;
        // Extract item2 from each object in the array
        this.loggedInUser.permissions = Array.isArray(permissionsArray)
          ? permissionsArray.map((item: any) => item.item2)
          : [];

        if (
          this.loggedInUser.permissions.some(
            (per) => per === UserPermission.BIC_ADMIN,
          ) &&
          !this.loggedInUser.permissions.includes(UserPermission.READ_ALL)
        ) {
          this.loggedInUser.permissions.push(UserPermission.READ_ALL);
        }
        this.permissionsLoaded = true;
      }),
      catchError((error) => {
        this.loggedInUser.permissions = [];
        this.permissionsLoaded = true;
        return of(void 0);
      }),
      map(() => void 0),
    );
  }

  /**
   * Checks if permissions have been loaded.
   *
   * @returns {boolean} True if permissions are loaded, false otherwise
   */
  arePermissionsLoaded(): boolean {
    return this.permissionsLoaded;
  }
}
